import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import Modal from "../components/Modal";
import ConfirmDialog from "../components/ConfirmDialog";

export default function Settings() {
  const { user, signOut, deleteAccount } = useAuth();
  const { properties, tenants, payments, resetAll } = useData();
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [clearText, setClearText] = useState("");
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [clearing, setClearing] = useState(false);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Settings</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage your profile, data and account preferences.
        </p>
      </div>

      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Profile</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Full name" value={user?.fullName} />
          <Field label="Email" value={user?.email} />
          <Field label="Auth method" value={user?.authMethod} />
          <Field label="User ID" value={user?.id} />
        </div>
      </div>

      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Data</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Clearing data permanently removes all properties, tenants, payments and communications.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat label="Properties" value={properties.length} />
          <Stat label="Tenants" value={tenants.length} />
          <Stat label="Payments" value={payments.length} />
        </div>
        <button
          onClick={() => setConfirmReset(true)}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white dark:bg-gray-800 dark:border-red-800 px-4 py-2.5 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
        >
          Clear all data
        </button>
      </div>

      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Security</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {user?.authMethod === "google"
            ? "You are signed in with Google. 2FA is managed by Google."
            : "Two-factor authentication is enabled for your email sign-ins."}
        </p>
      </div>

      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Session</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Sign out on this device.</p>
        <button onClick={() => setConfirmSignOut(true)} className="btn-secondary mt-4">
          Sign out
        </button>
      </div>

      <div className="card border-red-200 dark:border-red-900 p-6">
        <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">Danger zone</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Permanently delete your account and all associated data. This cannot be undone.
        </p>
        <button
          onClick={() => setConfirmDelete(true)}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
        >
          Delete account
        </button>
      </div>

      <ConfirmDialog
        open={confirmSignOut}
        onClose={() => setConfirmSignOut(false)}
        title="Sign out?"
        description="You'll need to sign back in to access your dashboard."
        confirmLabel="Sign out"
        cancelLabel="Stay signed in"
        tone="danger"
        onConfirm={() => {
          setConfirmSignOut(false);
          signOut();
        }}
      />

      <Modal
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setDeleteText("");
        }}
        title="Delete account?"
        description="This will delete your account and permanently remove all properties, tenants, payments, communications and history."
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            This action cannot be undone.
          </div>
          <div>
            <label className="label-base">
              Type your email{" "}
              <span className="font-mono font-semibold">{user?.email}</span> to confirm
            </label>
            <input
              type="text"
              className="input-base font-mono"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder={user?.email}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              className="btn-secondary"
              onClick={() => {
                setConfirmDelete(false);
                setDeleteText("");
              }}
            >
              Cancel
            </button>
            <button
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={deleting || deleteText.trim().toLowerCase() !== (user?.email || "").toLowerCase()}
              onClick={async () => {
                setDeleting(true);
                try {
                  await deleteAccount();
                } catch {
                  signOut();
                }
                setDeleting(false);
                setConfirmDelete(false);
                setDeleteText("");
              }}
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              {deleting ? "Deleting…" : "Delete account"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmReset}
        onClose={() => {
          setConfirmReset(false);
          setClearText("");
        }}
        title="Clear all data?"
        description="This removes all properties, tenants, payments and communications. You'll be returned to onboarding."
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            This action cannot be undone.
          </div>
          <div>
            <label className="label-base">
              Type <span className="font-mono font-semibold">CLEAR</span> to confirm
            </label>
            <input
              type="text"
              className="input-base font-mono"
              value={clearText}
              onChange={(e) => setClearText(e.target.value)}
              placeholder="CLEAR"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              className="btn-secondary"
              onClick={() => {
                setConfirmReset(false);
                setClearText("");
              }}
            >
              Cancel
            </button>
            <button
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={clearing || clearText !== "CLEAR"}
              onClick={async () => {
                setClearing(true);
                try {
                  await resetAll();
                } catch (err) {
                  console.error("Clear data failed:", err);
                }
                setClearing(false);
                setConfirmReset(false);
                setClearText("");
              }}
            >
              {clearing && <Loader2 className="h-4 w-4 animate-spin" />}
              {clearing ? "Clearing…" : "Clear data"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-sm text-gray-900 dark:text-white">{value || "—"}</div>
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-900 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}
